import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { AlertCircle, Clock } from 'react-feather';

const formatDate = ts => {
  if (!ts) return '';
  const d = new Date(parseInt(ts, 10));
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * SubscriptionExpiryBanner
 *
 * Renders a non-blocking banner at the top of the coach dashboard ONLY
 * when the coach's subscription is genuinely going to end soon — either
 * because they cancelled and are in the wind-down period, or because
 * their plan has already lapsed.
 *
 * State machine:
 *   - Not a coach (isProvider !== true)                     -> nothing
 *   - No subscriptionDetails on metadata                    -> nothing
 *   - isCancellationScheduled = true, expiryDate > now      -> amber banner
 *       ("Your subscription will end in <X> days")
 *   - expiryDate < now (genuinely expired)                  -> red banner
 *       ("Your subscription has expired. Renew now.")
 *   - Otherwise (active and renewing)                       -> nothing
 *
 * The previous version of this banner ALSO triggered "expires in X days"
 * for any subscription within 7 days of expiryDate. That misfired for
 * active auto-renewing subscriptions because expiryDate just represents
 * the next billing cycle, not the end of access — every coach saw the
 * banner once a month. Removed: per product spec, the banner is now
 * exclusively for users who genuinely need to know when access ends.
 *
 * Source of truth for "is cancellation pending":
 *   subscriptionDetails.isCancellationScheduled (boolean) — written by
 *   subscription-service/scripts/subscription.js when the Chargebee
 *   webhook reports `status === 'non_renewing'`. Webhook-driven, always
 *   reflects Chargebee's current view. After a reactivation,
 *   subscription_changed fires with status='active' → flag flips back
 *   to false → banner disappears.
 *
 *   We deliberately do NOT read the top-level `subscriptionCancellation`
 *   timestamp anymore — that field can persist as stale data across
 *   cancel/reactivate cycles, leading to false-positive banners on
 *   users who have a perfectly valid subscription.
 */
const SubscriptionExpiryBanner = ({ currentUser, history }) => {
  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider;
  if (!currentUser || !isProvider) return null;

  const subscriptionDetails =
    currentUser?.attributes?.profile?.metadata?.subscriptionDetails;

  // No subscription record at all - this typically means the coach hasn't
  // signed up for any plan yet, or their previous subscription was wiped
  // by the subscription_cancelled webhook. The SubscribeToBeVisibleBanner
  // covers tier 0; nothing for us to do here.
  if (!subscriptionDetails?.expiryDate) return null;

  const expiryMs = parseInt(subscriptionDetails.expiryDate, 10);
  if (!expiryMs || isNaN(expiryMs)) return null;

  const nowMs = Date.now();
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysRemaining = Math.ceil((expiryMs - nowMs) / msPerDay);

  const isExpired = daysRemaining < 0;
  const isCancellationPending = !!subscriptionDetails.isCancellationScheduled;

  // Show the banner ONLY when there's a real end-of-access event to
  // communicate: cancellation in the wind-down period, or already-lapsed
  // subscription. An active auto-renewing subscription stays silent.
  if (!isExpired && !isCancellationPending) {
    return null;
  }

  const goToBilling = () => history.push('/account/payments/pricing');

  // ---- Expired (red) ----
  if (isExpired) {
    return (
      <div className="p-4 mb-5 bg-red-50 border border-red-300 rounded-md flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
          <div>
            <p className="m-0 font-semibold text-red-900 text-base">
              Your Viveka subscription has expired
            </p>
            <p className="m-0 text-red-800 text-sm mt-1">
              It ended on {formatDate(expiryMs)}. Renew now to restore access
              to your dashboard and keep your listings active.
            </p>
          </div>
        </div>
        <button
          onClick={goToBilling}
          className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-md text-sm flex-shrink-0"
        >
          Renew now
        </button>
      </div>
    );
  }

  // ---- Cancellation pending (amber) ----
  // The early-return above guarantees we only reach this block when
  // `isCancellationPending` is true, so the "expires"/"Manage
  // subscription" fallback copy in the ternaries below is currently
  // unreachable. Kept as a defensive safety net in case the guard
  // logic above ever changes.
  let daysLabel;
  if (daysRemaining <= 0) {
    daysLabel = 'today';
  } else if (daysRemaining === 1) {
    daysLabel = 'tomorrow';
  } else {
    daysLabel = `in ${daysRemaining} days`;
  }

  const headline = isCancellationPending
    ? `Your subscription will end ${daysLabel}`
    : `Your subscription expires ${daysLabel}`;

  const detail = isCancellationPending
    ? `Ends on ${formatDate(expiryMs)}. Resubscribe to keep access to your dashboard, listings, and clients.`
    : `Expires on ${formatDate(expiryMs)}. Make sure your payment method is up to date to avoid losing access.`;

  const ctaLabel = isCancellationPending ? 'Resubscribe' : 'Manage subscription';

  return (
    <div className="p-4 mb-5 bg-amber-50 border border-amber-300 rounded-md flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Clock className="text-amber-600 flex-shrink-0" size={24} />
        <div>
          <p className="m-0 font-semibold text-amber-900 text-base">
            {headline}
          </p>
          <p className="m-0 text-amber-800 text-sm mt-1">{detail}</p>
        </div>
      </div>
      <button
        onClick={goToBilling}
        className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-4 py-2 rounded-md text-sm flex-shrink-0"
      >
        {ctaLabel}
      </button>
    </div>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default withRouter(connect(mapStateToProps)(SubscriptionExpiryBanner));
